/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { useApp } from "../contexts/AppContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Target, Check, Save } from "lucide-react";

/** Carte Accueil patient : objectifs hebdo fixés par le praticien + progression. */
export function GoalsCard() {
  const { patient, online } = useApp();
  const [data, setData] = useState(() => { try { return JSON.parse(localStorage.getItem("bc_goals") || "null"); } catch { return null; } });
  useEffect(() => {
    if (!patient) return;
    api.get(`/patients/${patient.id}/goals/progress`).then(({ data }) => { setData(data); localStorage.setItem("bc_goals", JSON.stringify(data)); }).catch(() => null);
  }, [patient, online]);
  if (!data?.goal) return null;
  return (
    <section data-testid="goals-card" className="bg-white rounded-3xl border border-[#E4E0D5] p-4">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2"><Target size={16} className="text-[#2BA3A0]" /><h3 className="text-[12px] uppercase tracking-wider text-[#0A1426]/60 font-bold">Objectifs de la semaine</h3></div>
        <span data-testid="goals-reached" className="text-[12px] font-bold text-[#0A1426]">{data.atteints}/{data.items.length}</span>
      </div>
      {data.goal.message && <p className="text-[13px] text-[#0A1426]/80 mb-3">« {data.goal.message} » — votre praticien</p>}
      <div className="flex flex-col gap-2.5">
        {data.items.map((it) => (
          <div key={it.code} data-testid={`goal-${it.code}`}>
            <div className="flex justify-between text-[13px] text-[#0A1426]">
              <span className="font-bold flex items-center gap-1">{it.atteint && <Check size={14} className="text-[#5CB85C]" />}{it.label}</span>
              <span>{it.fait ?? "—"}{it.unite || ""} / {it.cible}{it.unite || ""}</span>
            </div>
            <div className="h-3 bg-[#F5F3EE] rounded-full overflow-hidden mt-1">
              <div className="h-full rounded-full transition-all" style={{ width: `${it.pct}%`, backgroundColor: it.atteint ? "#5CB85C" : "#2BA3A0" }} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/** Formulaire praticien (fiche patient). */
export function GoalsForm({ patientId }) {
  const [form, setForm] = useState({ seances_hebdo: 5, tension_mesures_hebdo: 7, observance_pct: 80, message: "" });
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/patients/${patientId}/goals/progress`).then(({ data }) => {
    setProgress(data);
    if (data.goal) setForm({ seances_hebdo: data.goal.seances_hebdo, tension_mesures_hebdo: data.goal.tension_mesures_hebdo, observance_pct: data.goal.observance_pct, message: data.goal.message || "" });
  }).catch(() => null);
  useEffect(() => { load(); }, [patientId]); // eslint-disable-line
  const save = async () => {
    setBusy(true);
    try { await api.put(`/patients/${patientId}/goals`, { ...form, message: form.message || null }); toast.success("Objectifs enregistrés — visibles sur l'Accueil du patient"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };
  const Num = ({ k, label, max }) => (
    <label className="text-[12px] text-[#0A1426]/70 flex flex-col gap-1">{label}
      <input data-testid={`goal-input-${k}`} type="number" min="0" max={max} value={form[k]} onChange={(e) => setForm({ ...form, [k]: Number(e.target.value) })}
        className="h-11 px-4 rounded-full border border-[#E4E0D5] text-sm text-[#0A1426] font-bold" />
    </label>
  );
  return (
    <div data-testid="goals-form" className="bg-white rounded-2xl border border-[#E4E0D5] p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[11px] uppercase tracking-wider text-[#0A1426]/60 font-bold">Objectifs hebdo</h3>
        {progress?.goal && <span data-testid="goals-progress-pro" className="text-[11px] font-bold text-[#0A1426]">{progress.atteints}/{progress.items.length} atteints cette semaine</span>}
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Num k="seances_hebdo" label="Séances / sem." max={21} />
        <Num k="tension_mesures_hebdo" label="Mesures tension" max={21} />
        <Num k="observance_pct" label="Observance %" max={100} />
      </div>
      <input data-testid="goal-input-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Message d'encouragement (facultatif)"
        className="mt-2 h-11 w-full px-4 rounded-full border border-[#E4E0D5] text-sm text-[#0A1426]" maxLength={160} />
      {progress?.items && (
        <div className="mt-3 flex flex-col gap-1.5">
          {progress.items.map((it) => (
            <div key={it.code} className="flex items-center gap-2 text-[12px] text-[#0A1426]">
              <span className="w-28">{it.label}</span>
              <div className="flex-1 h-2 bg-[#F5F3EE] rounded-full overflow-hidden"><div className="h-full" style={{ width: `${it.pct}%`, backgroundColor: it.atteint ? "#5CB85C" : "#2BA3A0" }} /></div>
              <span className="w-16 text-right">{it.fait ?? "—"}{it.unite || ""}/{it.cible}{it.unite || ""}</span>
            </div>
          ))}
        </div>
      )}
      <Button data-testid="goals-save-btn" disabled={busy} onClick={save} className="w-full h-11 mt-3 rounded-full bg-[#0A1426] text-white font-bold"><Save size={16} className="mr-2" /> Enregistrer les objectifs</Button>
    </div>
  );
}
